/**
 * Data for the panel/battery model comparison tool
 * (content/pages/panel-battery-comparison.md). Real, currently-quoted UK
 * models rather than generic technology categories, so this needs
 * refreshing if a model gets discontinued or a spec sheet changes; it isn't
 * wired to any external spec feed.
 *
 * Panel specs are sense-checked across current UK solar comparison and
 * installer guides (not a single manufacturer datasheet each), so treat
 * warranty/efficiency figures as "what's typically quoted", not a precise
 * spec sheet, September 2026.
 *
 * Battery rows are the same five models, and the same figures, as
 * content/posts/home-battery-brands-compared.md's own table: this file
 * must never quote a different number for one of those five without
 * updating that post too, or the two pages contradict each other.
 */

export interface PanelModel {
  id: string;
  label: string;
  cellType: string;
  wattage: string;
  efficiencyRange: string;
  warranty: string;
  bestFor: string;
}

export const PANEL_MODELS: PanelModel[] = [
  {
    id: "ja-solar-deepblue",
    label: "JA Solar DeepBlue",
    cellType: "PERC",
    wattage: "445-455W",
    efficiencyRange: "20.5-21.5%",
    warranty: "12-year product / 25-year performance (84% at year 25)",
    bestFor: "A tight budget where efficiency matters less than upfront cost",
  },
  {
    id: "jinko-tiger-neo",
    label: "Jinko Solar Tiger Neo",
    cellType: "N-type TOPCon",
    wattage: "435-455W",
    efficiencyRange: "21-22%",
    warranty: "12-year product / 25 to 30-year performance",
    bestFor: "The default fit on many current UK quotes",
  },
  {
    id: "trina-vertex-s-plus",
    label: "Trina Solar Vertex S+",
    cellType: "N-type i-TOPCon",
    wattage: "440-450W",
    efficiencyRange: "22-23%",
    warranty: "25-year product and performance",
    bestFor: "More output per panel than a budget option, without moving to premium pricing",
  },
  {
    id: "longi-himo-x10",
    label: "LONGi Hi-MO X10",
    cellType: "N-type, HPBC 2.0 back-contact",
    wattage: "480-490W",
    efficiencyRange: "23-24%",
    warranty: "Typically 25-year performance",
    bestFor: "A smaller roof that needs more output from fewer panels",
  },
  {
    id: "rec-alpha-pure-r",
    label: "REC Alpha Pure-R",
    cellType: "Heterojunction (HJT)",
    wattage: "430-450W",
    efficiencyRange: "22-23%",
    warranty: "25-year product and performance, some ranges quoted to 40 years",
    bestFor: "The lowest degradation and best low-light output, at a higher price",
  },
  {
    id: "evo-max-super",
    label: "Project Solar Evo Max Super",
    cellType: "Monocrystalline, with a reflective rear surface",
    wattage: "455-480W (sources disagree on the exact figure)",
    efficiencyRange: "21-22%",
    warranty: "Marketed as a lifetime product warranty plus 10-year workmanship; ask directly who backs it",
    bestFor: "The in-house range from Project Solar UK, reportedly the UK's largest installer by volume",
  },
  // Maxeon Solar Technologies (the manufacturer behind the SunPower Maxeon
  // brand) filed for judicial management in Singapore on 1 April 2026 and
  // was delisted from Nasdaq on 1 May 2026 (per its own SEC 6-K filings).
  // That's directly relevant to a panel marketed on a 40-year warranty, so
  // it's called out in prose on both pages that list this model, not just
  // buried here. Recheck if the judicial management resolves one way or
  // the other.
  {
    id: "sunpower-maxeon",
    label: "SunPower Maxeon",
    cellType: "Monocrystalline (Maxeon/IBC cell)",
    wattage: "390-440W depending on generation (Maxeon 3/6/7)",
    efficiencyRange: "22-24.5%",
    warranty: "Marketed at up to 40-year product and performance; see the warranty note below the table",
    bestFor: "The highest efficiency on this list, if the warranty question below doesn't rule it out for you",
  },
];

export interface BatteryModel {
  id: string;
  label: string;
  chemistry: string;
  usableCapacity: string;
  cycleLifeWarranty: string;
  costGuide: string;
  bestFor: string;
}

export const BATTERY_MODELS: BatteryModel[] = [
  {
    id: "tesla-powerwall-3",
    label: "Tesla Powerwall 3",
    chemistry: "LFP",
    usableCapacity: "13.5kWh",
    cycleLifeWarranty: "10-year warranty, 70% capacity retained",
    costGuide: "£8,500 to £12,000 installed",
    bestFor: "One large single unit rather than several smaller modules",
  },
  {
    id: "puredrive-duracell",
    label: "Puredrive Duracell Dura5 / Dura16",
    chemistry: "LFP",
    usableCapacity: "5.12kWh / 14.4kWh",
    cycleLifeWarranty: "8,000 cycles, 10-year warranty",
    costGuide: "Mid-range; UK-designed and UK-warrantied",
    bestFor: "Wanting the warranty backed by a UK company directly",
  },
  {
    id: "solax-triple-power",
    label: "SolaX Triple Power",
    chemistry: "LFP",
    usableCapacity: "5.8kWh per module, stackable to 23kWh",
    cycleLifeWarranty: "Around 6,000 cycles, 10-year warranty",
    costGuide: "Roughly £1,200 to £1,900 per module before installation",
    bestFor: "Starting small and adding capacity in modules later",
  },
  {
    id: "sonnen-sonnenbatterie",
    label: "sonnen sonnenBatterie",
    chemistry: "LFP",
    usableCapacity: "5 to 15kWh depending on configuration",
    cycleLifeWarranty: "10,000 cycles, 10-year warranty (20-year design life)",
    costGuide: "No reliable UK price found; ask installers directly",
    bestFor: "The longest design life of this group, if the price works out",
  },
  {
    id: "enphase-iq-battery-5p",
    label: "Enphase IQ Battery 5P",
    chemistry: "LFP",
    usableCapacity: "5kWh, modular and AC-coupled",
    cycleLifeWarranty: "6,000 cycles or 15-year warranty",
    costGuide: "Sold and installed per module; pricing varies by installer",
    bestFor: "A microinverter-based (AC-coupled) array, since it's the option built for one",
  },
];

export function findPanelModel(id: string): PanelModel | undefined {
  return PANEL_MODELS.find((panel) => panel.id === id);
}

export function findBatteryModel(id: string): BatteryModel | undefined {
  return BATTERY_MODELS.find((battery) => battery.id === id);
}
